"use client";

import { posthogEvents } from "@studio/domain";
import { CheckCircle2, Eye, EyeOff, Mail } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { captureWebEvent } from "@/lib/analytics/posthog";
import { authClient } from "@/lib/auth-client";
// Referral feature is currently disabled — see lib/referral.ts.

export type AuthMode = "signin" | "signup";

interface AuthFormProps {
  mode: AuthMode;
  onRedirectingChange?: (redirecting: boolean) => void;
}

export function AuthForm({ mode, onRedirectingChange }: AuthFormProps) {
  const t = useTranslations("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set to true the moment we trigger a browser navigation (post-signin or
  // OAuth-redirect). Locks the form behind a full-card overlay so the user
  // can't double-click the button between when the spinner clears and when
  // the next page actually paints.
  const [redirecting, setRedirectingLocal] = useState(false);
  const setRedirecting = (next: boolean) => {
    setRedirectingLocal(next);
    onRedirectingChange?.(next);
  };

  // Signup-only state
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");

  // Signin-only state — the unverified-email recovery flow
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const posthog = usePostHog();

  const signinSchema = useMemo(
    () =>
      z.object({
        email: z.email(t("errors.invalidEmail")),
        password: z.string().min(8, t("errors.minPassword")),
      }),
    [t],
  );
  const signupSchema = useMemo(
    () =>
      z
        .object({
          email: z.email(t("errors.invalidEmail")),
          password: z.string().min(8, t("errors.minPassword")),
          confirmPassword: z.string(),
        })
        .refine((d) => d.password === d.confirmPassword, {
          message: t("errors.passwordsMismatch"),
          path: ["confirmPassword"],
        }),
    [t],
  );

  // Reset transient errors / success states when the user toggles modes so
  // an old "Check your email" or "User already exists" doesn't bleed into
  // the other form.
  useEffect(() => {
    setServerError(null);
    setEmailNotVerified(false);
    setVerificationSent(false);
    setSignupSuccess(false);
  }, [mode]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError(null);
    setEmailNotVerified(false);
    setVerificationSent(false);

    const parsed =
      mode === "signin"
        ? signinSchema.safeParse({ email, password })
        : signupSchema.safeParse({ email, password, confirmPassword });

    if (!parsed.success) {
      setServerError(parsed.error.issues[0].message);
      return;
    }

    setSubmitting(true);
    // Tracked outside React state so the `finally` block can read the
    // synchronous result of onSuccess without waiting for a re-render.
    let didRedirect = false;
    try {
      if (mode === "signin") {
        await authClient.signIn.email(
          { email, password },
          {
            onSuccess: () => {
              captureWebEvent({
                posthog,
                event: posthogEvents.loginCompleted,
                properties: { method: "email" },
              });
              // Lock the form before the navigation kicks off so the
              // brief window between this callback and the new page's
              // first paint can't surface an enabled button.
              didRedirect = true;
              setRedirecting(true);
              // Server-side gate on /onboarding handles the subscriber
              // bypass to /projects, so we always head there post-auth.
              window.location.href = "/onboarding";
            },
            onError: (ctx) => {
              const msg = ctx.error?.message || t("errors.loginGeneric");
              const isVerifyError =
                msg.toLowerCase().includes("email") &&
                (msg.toLowerCase().includes("not verified") ||
                  msg.toLowerCase().includes("verify"));
              if (isVerifyError) {
                setEmailNotVerified(true);
                setServerError(t("errors.emailNotVerified"));
              } else {
                setServerError(msg);
              }
            },
            fetchOptions: { onError: handleRateLimit("signin") },
          },
        );
      } else {
        await authClient.signUp.email(
          {
            email,
            password,
            name: email.split("@")[0],
            callbackURL: "/onboarding",
          },
          {
            onSuccess: async () => {
              captureWebEvent({
                posthog,
                event: posthogEvents.signupCompleted,
                properties: {
                  referral_code_present: false,
                  signup_method: "email",
                },
                appSurface: "marketing",
              });
              setSuccessEmail(email);
              setSignupSuccess(true);
            },
            onError: (ctx) => {
              let msg = ctx.error?.message || t("errors.signupGeneric");
              if (
                msg.toLowerCase().includes("already exists") ||
                msg.toLowerCase().includes("user already")
              ) {
                msg = t("errors.alreadyExists");
              }
              setServerError(msg);
            },
            fetchOptions: { onError: handleRateLimit("signup") },
          },
        );
      }
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : t("errors.unexpected"),
      );
    } finally {
      // Keep the spinner up if we're mid-redirect — the form has to stay
      // disabled until the new page paints. Otherwise the user sees a
      // brief enabled button and can re-click into a duplicate request.
      if (!didRedirect) setSubmitting(false);
    }
  };

  const handleRateLimit =
    (action: "signin" | "signup") => async (ctx: { response?: Response }) => {
      if (ctx.response?.status === 429) {
        const retryAfter = ctx.response.headers.get("X-Retry-After");
        const seconds = retryAfter ? Number.parseInt(retryAfter, 10) : 10;
        setServerError(
          action === "signin"
            ? t("errors.rateLimitedLogin", { seconds })
            : t("errors.rateLimitedSignup", { seconds }),
        );
      }
    };

  const handleSocial = async (provider: "google" | "apple" | "discord") => {
    setServerError(null);
    // Lock the form straight away — the OAuth provider redirect leaves
    // the page imminently and we don't want clicks landing on the
    // unlocked form before the browser unloads.
    setRedirecting(true);
    try {
      // Fire the analytics event eagerly — the OAuth redirect will unload
      // the page before we ever see a success callback. PostHog ships
      // batched events on beforeunload.
      captureWebEvent({
        posthog,
        event:
          mode === "signin"
            ? posthogEvents.loginCompleted
            : posthogEvents.signupCompleted,
        properties:
          mode === "signin"
            ? { method: provider, intent: "redirect" }
            : {
                referral_code_present: false,
                signup_method: provider,
                intent: "redirect",
              },
        appSurface: mode === "signin" ? "studio" : "marketing",
      });
      await authClient.signIn.social({ provider, callbackURL: "/onboarding" });
    } catch (err) {
      // OAuth init failed before the redirect happened — release the lock
      // so the user can retry.
      setRedirecting(false);
      setServerError(
        err instanceof Error
          ? err.message
          : t("errors.socialFailed", { provider }),
      );
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setServerError(t("errors.enterEmailFirst"));
      return;
    }
    setResendingVerification(true);
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: "/onboarding",
      });
      setVerificationSent(true);
      setServerError(null);
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : t("errors.verifyEmailFailed"),
      );
    } finally {
      setResendingVerification(false);
    }
  };

  if (signupSuccess) {
    return (
      <div className="auth-success">
        <div className="auth-success-icon">
          <Mail aria-hidden />
        </div>
        <h2 className="auth-success-title">{t("signupSuccess.title")}</h2>
        <p className="auth-success-body">
          {t("signupSuccess.bodyBefore")}
          <strong>{successEmail}</strong>
          {t("signupSuccess.bodyAfter")}
        </p>
        <p className="auth-success-meta">
          <CheckCircle2 className="auth-success-meta-icon" aria-hidden />
          {t("signupSuccess.meta")}
        </p>
        <Link href="/resend-verification" className="auth-link-muted">
          {t("signupSuccess.resend")}
        </Link>
      </div>
    );
  }

  return (
    <form
      className="auth-form"
      onSubmit={handleSubmit}
      noValidate
      aria-busy={redirecting || undefined}
    >
      {verificationSent && (
        <div className="auth-banner auth-banner-success">
          <Mail className="auth-banner-icon" aria-hidden />
          <span>{t("verificationSent")}</span>
        </div>
      )}

      {serverError && (
        <div className="auth-banner auth-banner-error">
          <span>{serverError}</span>
          {emailNotVerified && !verificationSent && (
            <button
              type="button"
              className="auth-link"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification
                ? t("resendingVerification")
                : t("resendVerification")}
            </button>
          )}
        </div>
      )}

      <label className="auth-field">
        <span className="auth-label">{t("labels.email")}</span>
        <input
          type="email"
          autoComplete="email"
          required
          className="auth-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          placeholder={t("labels.emailPlaceholder")}
        />
      </label>

      <label className="auth-field">
        <span className="auth-label">{t("labels.password")}</span>
        <div className="auth-input-wrap">
          <input
            type={showPassword ? "text" : "password"}
            autoComplete={
              mode === "signin" ? "current-password" : "new-password"
            }
            required
            minLength={8}
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
          <button
            type="button"
            className="auth-input-toggle"
            onClick={() => setShowPassword((s) => !s)}
            tabIndex={-1}
            aria-label={
              showPassword ? t("labels.hidePassword") : t("labels.showPassword")
            }
          >
            {showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          </button>
        </div>
        {mode === "signup" && (
          <span className="auth-field-hint">{t("labels.passwordHint")}</span>
        )}
      </label>

      {/* Slot reserved at signup-mode height in both modes so the switcher
          and email + password fields above stay anchored when the user
          toggles. Sign-in mode parks the Forgot password link inside it
          and pads the rest with whitespace; signup mode fills it with the
          confirm-password field. */}
      <div className={`auth-mode-slot auth-mode-slot-${mode}`}>
        {mode === "signup" ? (
          <label className="auth-field">
            <span className="auth-label">{t("labels.confirmPassword")}</span>
            <div className="auth-input-wrap">
              <input
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={8}
                className="auth-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowConfirmPassword((s) => !s)}
                tabIndex={-1}
                aria-label={
                  showConfirmPassword
                    ? t("labels.hidePassword")
                    : t("labels.showPassword")
                }
              >
                {showConfirmPassword ? (
                  <EyeOff aria-hidden />
                ) : (
                  <Eye aria-hidden />
                )}
              </button>
            </div>
          </label>
        ) : (
          <Link href="/forgot-password" className="auth-link-muted auth-forgot">
            {t("labels.forgot")}
          </Link>
        )}
      </div>

      <button
        type="submit"
        className="ob-btn ob-btn-primary auth-submit"
        disabled={submitting}
      >
        {submitting ? (
          <>
            <span className="ob-btn-spinner" aria-hidden />
            {mode === "signin"
              ? t("submit.signinPending")
              : t("submit.signupPending")}
          </>
        ) : (
          <>
            {mode === "signin" ? t("submit.signin") : t("submit.signup")}
            <svg
              className="icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="13 6 19 12 13 18" />
            </svg>
          </>
        )}
      </button>

      <div className="auth-divider">
        <span>{t("divider")}</span>
      </div>

      <div className="auth-socials">
        <button
          type="button"
          className="auth-social"
          onClick={() => handleSocial("apple")}
          disabled={submitting}
          aria-label={t("social.ariaApple")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
              fill="currentColor"
            />
          </svg>
          <span>{t("social.apple")}</span>
        </button>
        <button
          type="button"
          className="auth-social"
          onClick={() => handleSocial("google")}
          disabled={submitting}
          aria-label={t("social.ariaGoogle")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
              fill="currentColor"
            />
          </svg>
          <span>{t("social.google")}</span>
        </button>
        <button
          type="button"
          className="auth-social"
          onClick={() => handleSocial("discord")}
          disabled={submitting}
          aria-label={t("social.ariaDiscord")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
          </svg>
          <span>{t("social.discord")}</span>
        </button>
      </div>

      <p className="auth-fineprint">
        {mode === "signup"
          ? t("fineprint.signupBefore")
          : t("fineprint.signinBefore")}
        <Link href="/terms" className="auth-link-muted">
          {t("fineprint.termsLink")}
        </Link>
        {t("fineprint.and")}
        <Link href="/privacy" className="auth-link-muted">
          {t("fineprint.privacyLink")}
        </Link>
        {t("fineprint.end")}
      </p>
    </form>
  );
}
